// Normalized voter identity for the display path. The vote pipeline is
// historically email-keyed and DROPS emailless voters — a guest (anonymous uid,
// no email) would move tallies but vanish from voter stacks. normalizeVoter
// Identity keys on uid/id (with guestName/displayName fallbacks) so a guest
// renders as a real voter; dedupeVotersById dedupes by id (email fallback) and
// never drops a user for lacking an email.
//
// Spec §9: guest displayName = guestName; email/avatar absent for guests.

const NEUTRAL_LABEL = "Anonymous voter";

export function normalizeVoterIdentity(voteDoc) {
  const id = voteDoc?.id ?? null;
  const isGuest = voteDoc?.isGuest === true;
  const displayName =
    voteDoc?.guestName ||
    voteDoc?.displayName ||
    voteDoc?.userEmail ||
    NEUTRAL_LABEL;

  // Only carry email/avatar when actually present — a guest doc has neither,
  // and the rules whitelist forbids a guest from writing them. Omitting the
  // keys entirely (rather than emitting undefined) keeps the guest object clean
  // for the anonymization layer and snapshot tests.
  const out = { id, displayName, isGuest };
  const email = voteDoc?.userEmail;
  if (email) out.email = email;
  const avatar = voteDoc?.userAvatar;
  if (avatar) out.avatar = avatar;
  return out;
}

// Dedupe by id (preferred), falling back to email only when id is absent.
// A user with neither id nor email is still kept exactly once (synthetic key),
// so an emailless guest is never silently dropped. Preserves first occurrence.
export function dedupeVotersById(users) {
  if (!Array.isArray(users)) return [];
  const map = new Map();
  let synthetic = 0;
  users.forEach((user) => {
    const id = user?.id;
    const email = user?.email;
    const key = id || email || `__noid-${synthetic++}`;
    if (!map.has(key)) map.set(key, user);
  });
  return Array.from(map.values());
}
